import { useState, useRef, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useSiteConfig } from "@/context/SiteConfigContext";

const serviceCategories = [
  {
    label: "Residential Services",
    items: [
      { name: "Residential Junk Removal", slug: "residential-junk-removal" },
      { name: "Furniture Removal", slug: "furniture-removal" },
      { name: "Appliance Removal", slug: "appliance-removal" },
      { name: "Garage Cleanouts", slug: "garage-cleanouts" },
      { name: "Attic Cleanouts", slug: "attic-cleanouts" },
      { name: "Basement Cleanouts", slug: "basement-cleanouts" },
      { name: "Yard Waste Removal", slug: "yard-waste-removal" },
      { name: "Garden Cleanouts", slug: "garden-cleanouts" },
      { name: "Hot Tub Removal", slug: "hot-tub-removal" },
    ],
  },
  {
    label: "Property & Specialty Cleanouts",
    items: [
      { name: "Estate Cleanouts", slug: "estate-cleanouts" },
      { name: "Foreclosure Cleanouts", slug: "foreclosure-cleanouts" },
      { name: "Hoarder Cleanouts", slug: "hoarder-cleanouts" },
      { name: "Moving Cleanouts", slug: "moving-cleanouts" },
      { name: "Shed Cleanouts", slug: "shed-cleanouts" },
      { name: "Deck Removals", slug: "deck-removals" },
      { name: "Playground Removals", slug: "playground-removals" },
    ],
  },
  {
    label: "Commercial Services",
    items: [
      { name: "Office & Commercial Cleanouts", slug: "office-commercial-cleanouts" },
      { name: "Construction Debris Removal", slug: "construction-debris-removal" },
    ],
  },
  {
    label: "Additional Services",
    items: [
      { name: "Material Delivery", slug: "material-delivery" },
      { name: "Light Demolition", slug: "light-demolition" },
    ],
  },
];

const navLinks = [
  { name: "Home", path: "/" },
  { name: "About", path: "/about" },
  { name: "Services", path: "/services", hasDropdown: true },
  { name: "Areas Served", path: "/locations" },
  { name: "Gallery", path: "/gallery" },
  { name: "Testimonials", path: "/testimonials" },
  { name: "Discounts", path: "/community-appreciation-discount" },
  { name: "Clutter News", path: "/blog" },
  { name: "Contact", path: "/contact" },
];

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const location = useLocation();
  const { formatted_phone, phone_link } = useSiteConfig();
  const dropdownRef = useRef(null);
  const timeoutRef = useRef(null);

  const isActive = (path) => location.pathname === path;

  // Close dropdown on route change
  useEffect(() => {
    setServicesOpen(false);
    setMobileMenuOpen(false);
    setMobileServicesOpen(false);
  }, [location.pathname]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setServicesOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const handleMouseEnter = () => {
    clearTimeout(timeoutRef.current);
    setServicesOpen(true);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => setServicesOpen(false), 200);
  };

  return (
    <header
      data-testid="main-header"
      className="fixed top-0 left-0 right-0 z-50 bg-white shadow-md"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" data-testid="logo-link" className="flex-shrink-0">
            <img
              src="/images/overhaul-ops-logo-128.png"
              alt="Overhaul Ops LLC - Firefighter Owned Junk Removal Company DFW Texas"
              className="h-16 w-auto object-contain"
              width="64"
              height="64"
              loading="eager"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden xl:flex items-center gap-1">
            {navLinks.map((link) =>
              link.hasDropdown ? (
                <div
                  key={link.path}
                  ref={dropdownRef}
                  className="relative"
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                >
                  <Link
                    to={link.path}
                    data-testid="nav-services"
                    className={`nav-link px-3 py-2 font-medium text-xs uppercase tracking-wide transition-colors whitespace-nowrap inline-flex items-center gap-1 ${
                      isActive(link.path)
                        ? "text-[#b51a00]"
                        : "text-[#4a4a4a] hover:text-[#b51a00]"
                    }`}
                  >
                    {link.name}
                    <ChevronDown className={`w-3 h-3 transition-transform ${servicesOpen ? "rotate-180" : ""}`} />
                  </Link>

                  {/* Mega Dropdown */}
                  {servicesOpen && (
                    <div
                      data-testid="services-dropdown"
                      className="absolute top-full left-1/2 -translate-x-1/2 mt-0 bg-white rounded-sm shadow-xl border border-gray-100 py-6 px-8 min-w-[640px] grid grid-cols-2 gap-6"
                    >
                      {serviceCategories.map((cat) => (
                        <div key={cat.label}>
                          <h4
                            className="text-xs font-bold uppercase tracking-widest text-[#b51a00] mb-3"
                            style={{ fontFamily: "Barlow Condensed, sans-serif" }}
                          >
                            {cat.label}
                          </h4>
                          <ul className="space-y-1.5">
                            {cat.items.map((item) => (
                              <li key={item.slug}>
                                <Link
                                  to={`/services/${item.slug}`}
                                  className="text-sm text-[#4a4a4a] hover:text-[#b51a00] transition-colors block py-0.5"
                                >
                                  {item.name}
                                </Link>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`nav-${link.name.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`nav-link px-3 py-2 font-medium text-xs uppercase tracking-wide transition-colors whitespace-nowrap ${
                    isActive(link.path)
                      ? "text-[#b51a00]"
                      : "text-[#4a4a4a] hover:text-[#b51a00]"
                  }`}
                >
                  {link.name}
                </Link>
              )
            )}
          </nav>

          {/* CTA and Phone */}
          <div className="hidden xl:flex items-center gap-3">
            <a
              href={phone_link}
              data-testid="header-phone"
              className="flex items-center gap-2 text-[#4a4a4a] hover:text-[#b51a00] transition-colors"
            >
              <Phone className="w-4 h-4" />
              <span className="font-medium text-sm">{formatted_phone}</span>
            </a>
            <Link to="/contact">
              <Button
                data-testid="header-quote-btn"
                className="bg-[#b51a00] hover:bg-[#8a1300] text-white font-bold uppercase tracking-wider rounded-sm px-4 py-2 text-sm"
              >
                Get Quote
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-btn"
            className="xl:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? (
              <X className="w-6 h-6 text-[#0f0f0f]" />
            ) : (
              <Menu className="w-6 h-6 text-[#0f0f0f]" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div
          data-testid="mobile-menu"
          className="xl:hidden bg-white border-t shadow-lg max-h-[80vh] overflow-y-auto"
        >
          <nav className="px-4 py-4 space-y-1">
            {navLinks.map((link) =>
              link.hasDropdown ? (
                <div key={link.path}>
                  <button
                    data-testid="mobile-nav-services"
                    onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
                    className={`w-full flex items-center justify-between py-3 px-4 font-medium uppercase tracking-wider text-sm ${
                      isActive(link.path)
                        ? "bg-[#b51a00] text-white"
                        : "text-[#4a4a4a] hover:bg-gray-100"
                    }`}
                  >
                    {link.name}
                    <ChevronDown className={`w-4 h-4 transition-transform ${mobileServicesOpen ? "rotate-180" : ""}`} />
                  </button>
                  {mobileServicesOpen && (
                    <div className="bg-gray-50 py-2 px-4">
                      {serviceCategories.map((cat) => (
                        <div key={cat.label} className="mb-3">
                          <p className="text-xs font-bold uppercase tracking-widest text-[#b51a00] mb-1 px-4">
                            {cat.label}
                          </p>
                          {cat.items.map((item) => (
                            <Link
                              key={item.slug}
                              to={`/services/${item.slug}`}
                              onClick={() => setMobileMenuOpen(false)}
                              className="block py-1.5 px-4 text-sm text-[#4a4a4a] hover:text-[#b51a00]"
                            >
                              {item.name}
                            </Link>
                          ))}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`mobile-nav-${link.name.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`block py-3 px-4 font-medium uppercase tracking-wider text-sm ${
                    isActive(link.path)
                      ? "bg-[#b51a00] text-white"
                      : "text-[#4a4a4a] hover:bg-gray-100"
                  }`}
                >
                  {link.name}
                </Link>
              )
            )}
            <a
              href={phone_link}
              className="flex items-center gap-2 py-3 px-4 text-[#b51a00] font-bold"
            >
              <Phone className="w-5 h-5" />
              {formatted_phone}
            </a>
          </nav>
        </div>
      )}
    </header>
  );
}
